/** Master form school categories (`target_schools.name`): Independent, Grammar, Both. */

export const SCHOOL_TYPE_OPTIONS = [
  { value: "independent", label: "Independent" },
  { value: "grammar", label: "Grammar" },
  { value: "both", label: "Both" },
];

export function schoolCategoryKey(school) {
  return String(school?.category || "").trim().toLowerCase();
}

/** Independent and Grammar each include schools marked Both. Both is only that category. */
export function schoolMatchesCategory(school, category) {
  if (!category) return true;
  const name = schoolCategoryKey(school);
  if (category === "grammar") return name === "grammar" || name === "grammer" || name === "both";
  if (category === "independent") return name === "independent" || name === "both";
  if (category === "both") return name === "both";
  return true;
}

function typeHeading(school) {
  const name = schoolCategoryKey(school);
  if (name === "independent") return "Independent";
  if (name === "grammar" || name === "grammer") return "Grammar";
  if (name === "both") return "Both";
  return "Other";
}

function typeRank(heading) {
  if (heading === "Independent") return 0;
  if (heading === "Grammar") return 1;
  if (heading === "Both") return 2;
  return 3;
}

function schoolCountLabel(count) {
  return `${count} school${count === 1 ? "" : "s"}`;
}

function schoolsMatchingFilters(schools, { category = "", regionId = "", nation = "" } = {}) {
  return (schools || []).filter((school) => {
    if (!schoolMatchesCategory(school, category)) return false;
    if (regionId && String(school.region_id) !== String(regionId)) return false;
    if (nation && school.nation && school.nation !== nation) return false;
    return true;
  });
}

/**
 * Distinct consortiums among schools matching the current type / region / nation filters.
 */
export function consortiumOptionsForMenu(schools, { category = "", regionId = "", nation = "" } = {}) {
  const byId = new Map();
  schoolsMatchingFilters(schools, { category, regionId, nation }).forEach((school) => {
    if (!school?.consortium_id) return;
    const id = String(school.consortium_id);
    if (!byId.has(id)) {
      byId.set(id, {
        id: school.consortium_id,
        name: school.consortium_short || school.consortium || "Consortium",
        count: 0,
      });
    }
    byId.get(id).count += 1;
  });
  return [...byId.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

/**
 * Same exam / area list as the public Practice Papers and Mock Exams Consortium filter:
 * consortium tests first, then each non-consortium school (a school in a consortium is
 * reached by picking that consortium). Options follow School type.
 */
export function examAreaOptionsForMenu(schools, { category = "" } = {}) {
  const schoolsOfType = schoolsMatchingFilters(schools, { category });
  const consortiums = consortiumOptionsForMenu(schoolsOfType);
  const tests = consortiums.map((c) => ({
    id: String(c.id),
    name: `${c.name} (${schoolCountLabel(c.count)})`,
    count: c.count,
  }));
  const singles = schoolsOfType
    .filter((s) => s.consortium_id == null || s.consortium_id === "")
    .slice()
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
    .map((s) => ({ id: `school:${s.id}`, name: s.name }));
  const consortiumGroup = `Consortium (${schoolCountLabel(consortiums.reduce((sum, c) => sum + c.count, 0))})`;
  const singleGroup = `Non-consortium schools (${schoolCountLabel(singles.length)})`;
  return [
    ...tests.map((c) => ({ ...c, group: consortiumGroup })),
    ...singles.map((s) => ({ ...s, group: singleGroup })),
  ];
}

/** Consortium id, or `school:{id}` when the exam / area is a non-consortium school. */
export function examAreaSelectValue(filters) {
  if (filters?.exam_school_id) return `school:${filters.exam_school_id}`;
  if (filters?.consortium_id) return String(filters.consortium_id);
  return "";
}

/** Apply the public exam / area picker to admin list filters. */
export function applyExamAreaToFilters(filters, value, schools = []) {
  const raw = String(value || "");
  if (!raw) {
    return { ...filters, consortium_id: "", exam_school_id: "" };
  }
  if (raw.startsWith("school:")) {
    const exam_school_id = raw.slice("school:".length);
    const school_id = String(filters.school_id) === String(exam_school_id) ? filters.school_id : "";
    return { ...filters, consortium_id: "", exam_school_id, school_id };
  }
  const kept = schools.find((school) => String(school.id) === String(filters.school_id));
  const school_id = kept && String(kept.consortium_id) === raw ? filters.school_id : "";
  return { ...filters, consortium_id: raw, exam_school_id: "", school_id };
}

/** Keep the exam / area pick only when it still appears under the current school type. */
export function keptExamArea(schools, filters, extra = {}) {
  const next = { ...filters, ...extra };
  const value = examAreaSelectValue(next);
  if (!value) return { consortium_id: "", exam_school_id: "" };
  const still = examAreaOptionsForMenu(schools, { category: next.school_category }).some(
    (option) => String(option.id) === value
  );
  if (still) {
    return {
      consortium_id: next.consortium_id || "",
      exam_school_id: next.exam_school_id || "",
    };
  }
  return { consortium_id: "", exam_school_id: "" };
}

/** school_id / consortium_id / exam_school_id → the same query the public shop sends. */
export function appendSchoolAssessmentParams(params, filters) {
  if (filters.school_category) params.append("school_category", filters.school_category);
  if (filters.consortium_id) params.append("consortium_id", filters.consortium_id);
  if (filters.exam_school_id) params.append("school_id", filters.exam_school_id);
  if (filters.school_id) {
    if (filters.exam_school_id) params.append("target_school_ids", filters.school_id);
    else params.append("school_id", filters.school_id);
  }
  if (filters.region_id) params.append("region_id", filters.region_id);
  if (filters.nation) params.append("nation", filters.nation);
}

/**
 * Schools for a dropdown: Independent, then Grammar, then Both.
 * Within each type, consortium schools before non-consortium schools.
 */
export function groupSchoolsForMenu(schools, category = "", consortiumId = "", extra = {}) {
  const examSchoolId = extra.examSchoolId || extra.exam_school_id || "";
  const regionId = extra.regionId || extra.region_id || "";
  const nation = extra.nation || "";
  const groups = new Map();
  (schools || [])
    .filter((school) => schoolMatchesCategory(school, category))
    .filter((school) => !consortiumId || String(school.consortium_id) === String(consortiumId))
    .filter((school) => !examSchoolId || String(school.id) === String(examSchoolId))
    .filter((school) => !regionId || String(school.region_id) === String(regionId))
    .filter((school) => !nation || !school.nation || school.nation === nation)
    .forEach((school) => {
      const type = typeHeading(school);
      const consortiumName = school.consortium_short || school.consortium || "Consortium";
      const side = school.consortium_id ? `Consortium · ${consortiumName}` : "Non-consortium";
      const label = `${type} · ${side}`;
      if (!groups.has(label)) {
        groups.set(label, {
          label,
          rank: typeRank(type) * 10 + (school.consortium_id ? 0 : 1),
          schools: [],
        });
      }
      groups.get(label).schools.push(school);
    });

  return [...groups.values()]
    .sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label))
    .map((group) => ({
      ...group,
      schools: group.schools.slice().sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    }));
}
